import { useState, useEffect, useMemo } from 'react';
// Place status helper for canonical public check
import { isPlacePublic } from '@/lib/status';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, Save, Globe, EyeOff, Sparkles, Copy, Lock } from 'lucide-react';
import { ShareKit } from '@/components/producer/ShareKit';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { ShareStudioLite } from '@/features/share/ShareStudioLite';
import { PublishSuccessPrompt } from '@/features/share/PublishSuccessPrompt';
import { EntityShareAction } from '@/features/share/EntityShareAction';
import { mapActivityToShareTarget } from '@/features/share/mappers/mapActivityToShareTarget';
import { features } from '@/config/features';
import { ActivityStatsPanel } from '@/components/producer/ActivityStatsPanel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { track } from '@/lib/analytics';
import { classifyFailure } from '@/lib/analytics-helpers';
import { captureAppError } from '@/lib/monitoring/sentry';
import { shouldReportToSentry } from '@/lib/monitoring/sentryGate';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import { getCriticalChanges, getCriticalChangeLabels } from '@/lib/events/criticalChanges';
import { CriticalChangesDialog } from '@/components/events/CriticalChangesDialog';
import { supabase } from '@/integrations/supabase/client';

import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SaveButton } from '@/components/ui/SaveButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityTemplatePicker } from '@/components/events/ActivityTemplatePicker';
import type { ActivityTemplate } from '@/lib/activities/activityTemplates';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { ActivityComposer } from '@/components/events/ActivityComposer';
import { ActivityComposerFooter } from '@/components/events/ActivityComposerFooter';
import { UnsavedChangesDialog } from '@/components/events/UnsavedChangesDialog';
import {
  eventFormSchema,
  defaultEventFormValues,
  type EventFormData,
} from '@/components/events/eventFormSchema';
import { useProducer } from '@/hooks/useProducer';
import { useProducerEvent, useCreateEvent, useUpdateEvent, PlanLimitError } from '@/hooks/useProducerEvents';
import { useEventOccurrences, useSyncOccurrences } from '@/hooks/useEventOccurrences';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { resolveAssetUrl, deleteAsset, finalizeDraftSession } from '@/lib/media/mediaApi';
import { useDraftSessionId, clearDraftSessionId } from '@/hooks/useDraftSessionId';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import { canPublishEvent, checkPublishGuard, getPublishBlockReason, parsePublishError } from '@/lib/producer/publishGuard';
import { isPastActivity } from '@/lib/events/isPastActivity';
import { useCloneActivity } from '@/hooks/useCloneActivity';
import { activityDetailPath, activityEmbedPath } from '@/lib/paths';
import type { EventStatus, EventType } from '@/types/events';
import type { ActivityShareTarget } from '@/features/share/ShareTarget';

export default function EventEditorPage() {
  const queryClient = useQueryClient();
  const { farmId, eventId } = useParams<{ farmId: string; eventId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditing = !!eventId;
  const initialEventType = (!isEditing && searchParams.get('type')) || 'event';

  const { producer, loading: producerLoading } = useProducer(farmId || '');
  const { data: existingEvent, isLoading: eventLoading } = useProducerEvent(eventId);
  const { data: existingOccurrences } = useEventOccurrences(eventId);
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const syncOccurrences = useSyncOccurrences();
  const cloneActivity = useCloneActivity();

  // Determine if this is a past activity (read-only)
  const eventIsPast = isEditing && existingEvent
    ? isPastActivity({
        event_type: existingEvent.event_type,
        timing_mode: (existingEvent as any).timing_mode,
        start_date: existingEvent.start_date,
        end_date: existingEvent.end_date,
      })
    : false;

  const [showUnpublishDialog, setShowUnpublishDialog] = useState(false);
  const [showShareStudio, setShowShareStudio] = useState(false);
  const [showShareLite, setShowShareLite] = useState(false);
  const [showPublishSuccess, setShowPublishSuccess] = useState(false);
  const [showCriticalDialog, setShowCriticalDialog] = useState(false);
  const [criticalLabels, setCriticalLabels] = useState<string[]>([]);
  const [criticalFields, setCriticalFields] = useState<string[]>([]);
  const [pendingSaveStatus, setPendingSaveStatus] = useState<'draft' | 'public' | undefined>(undefined);
  // Track the last published event data for the share prompt
  const [publishedEventData, setPublishedEventData] = useState<{ id: string; title: string; startDate?: string | null } | null>(null);

  // Fetch booked_spots for published events
  const { data: bookedCount = 0 } = useQuery({
    queryKey: ['event-booked-count', eventId],
    queryFn: async () => {
      if (!eventId) return 0;
      const { count, error } = await supabase
        .from('event_bookings')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .eq('status', 'confirmed');
      if (error) { console.warn('Failed to fetch booking count', error); return 0; }
      return count ?? 0;
    },
    enabled: !!eventId && isEditing,
  });

  const draftSessionId = useDraftSessionId(`event-editor-${eventId ?? 'new'}`);
  const coverAsset = useCoverAsset('event', eventId);

  const [newModeEventId, setNewModeEventId] = useState<string | undefined>(undefined);
  const effectiveEventId = isEditing ? eventId : newModeEventId;

  // ── Template picker state ──
  const [selectedTemplate, setSelectedTemplate] = useState<ActivityTemplate | null>(null);
  const [templatePicked, setTemplatePicked] = useState(isEditing);

  // Producer offerings for smart sorting
  const { offeringRows } = useProducerOfferings(producer?.id);
  const offeringKeys = (offeringRows ?? []).map(r => r.offering_key);

  // Media pipeline
  const coverUpload = useMediaUpload({
    ownerType: 'event',
    ownerId: effectiveEventId ?? null,
    draftSessionId,
    kind: 'cover',
    maxFiles: 1,
  });

  const galleryUpload = useMediaUpload({
    ownerType: 'event',
    ownerId: effectiveEventId ?? null,
    draftSessionId,
    kind: 'gallery',
    maxFiles: 12,
  });

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: { ...defaultEventFormValues, event_type: initialEventType as any },
    mode: 'onChange',
  });

  // Capability: can this producer offer farm sale?
  // TEMPORARY: reads legacy booleans synced via trigger — pending migration to direct offering check
  const canOfferFarmSale = producer
    ? (producer.has_farm_sale_alcohol || producer.has_farm_shop)
    : false;
  const canOfferAlcohol = producer
    ? producer.has_farm_sale_alcohol
    : false;

  // Smart default: pre-check farm sale toggles when producer loads for new events
  const [smartDefaultApplied, setSmartDefaultApplied] = useState(false);
  useEffect(() => {
    if (isEditing || smartDefaultApplied || !producer) return;
    form.setValue('includes_farm_sale', canOfferFarmSale, { shouldDirty: false, shouldTouch: false });
    // Alcohol defaults to false always (avoid promising alcohol by default)
    form.setValue('includes_farm_sale_alcohol', false, { shouldDirty: false, shouldTouch: false });
    setSmartDefaultApplied(true);
  }, [producer, isEditing, smartDefaultApplied, form, canOfferFarmSale]);

  // Leakage guard: force false when producer lacks capability
  useEffect(() => {
    if (!producer) return;
    if (!canOfferFarmSale) {
      form.setValue('includes_farm_sale', false, { shouldDirty: false, shouldTouch: false });
      form.setValue('includes_farm_sale_alcohol', false, { shouldDirty: false, shouldTouch: false });
    } else if (!canOfferAlcohol) {
      form.setValue('includes_farm_sale_alcohol', false, { shouldDirty: false, shouldTouch: false });
    }
  }, [producer?.id, canOfferFarmSale, canOfferAlcohol, form]);

  const { isDirty } = form.formState;
  const originalStatus = existingEvent?.status as 'draft' | 'public' | undefined;
  const isPublished = originalStatus === 'public';

  const activityEmbedSnippet = useMemo(() => {
    if (!existingEvent?.id) return null;
    const src = `${window.location.origin}${activityEmbedPath(existingEvent.id)}`;
    return `<iframe\n  src="${src}"\n  width="100%"\n  height="420"\n  style="border:0;"\n  loading="lazy"\n></iframe>`;
  }, [existingEvent?.id]);

  const activityPublicUrl = useMemo(() => {
    if (!existingEvent?.id) return null;
    return `${window.location.origin}${activityDetailPath(existingEvent.id)}`;
  }, [existingEvent?.id]);

  // Fetch linked place (P0 INVARIANT: activities cannot publish if place is draft)
  const placeId = producer?.place_id ?? null;
  const { data: linkedPlace } = useQuery({
    queryKey: ['place', placeId],
    queryFn: async () => {
      if (!placeId) return null;
      const { data, error } = await supabase
        .from('places')
        .select('id, status')
        .eq('id', placeId)
        .maybeSingle();
      if (error) throw error;
      return data as { id: string; status: string } | null;
    },
    enabled: !!placeId,
  });

  const publishGuard = checkPublishGuard(producer, farmId, linkedPlace);
  const publishAllowed = publishGuard.ok;
  const publishBlock = getPublishBlockReason(producer, farmId, linkedPlace);

  const { 
    isConfirmOpen, 
    requestNavigate, 
    confirmNavigation, 
    cancelNavigation 
  } = useUnsavedChangesGuard({ isDirty });

  // Populate form with existing event data when editing – runs once per event ID
  const [formInitId, setFormInitId] = useState<string | null>(null);

  useEffect(() => {
    if (!existingEvent || !isEditing) return;
    // Only reset form once per event – prevent refetch from wiping user edits
    if (formInitId === existingEvent.id) return;
    // For recurring events, wait until occurrences have loaded so we can hydrate the calendar
    if (existingEvent.is_recurring && !existingOccurrences) return;

    const startDateStr = existingEvent.start_date
      ? format(toZonedTime(new Date(existingEvent.start_date), TZ), 'yyyy-MM-dd')
      : '';
    const startTime = existingEvent.start_date
      ? format(toZonedTime(new Date(existingEvent.start_date), TZ), 'HH:mm')
      : '';
    const endDateStr = existingEvent.end_date
      ? format(toZonedTime(new Date(existingEvent.end_date), TZ), 'yyyy-MM-dd')
      : '';
    const rawEndTime = existingEvent.end_date
      ? format(toZonedTime(new Date(existingEvent.end_date), TZ), 'HH:mm')
      : '';

    // Option 2: if end === start the user never chose an end time → treat as empty
    const hasDistinctEnd = !!(existingEvent.end_date && existingEvent.start_date
      && existingEvent.end_date !== existingEvent.start_date);
    const endTime = hasDistinctEnd ? rawEndTime : '';

    const isMultiDay = hasDistinctEnd && startDateStr !== endDateStr;
    const endsNextDay = hasDistinctEnd && !isMultiDay && rawEndTime < startTime;

    // Use stored timing_mode when available; only infer for legacy rows
    const storedTimingMode = (existingEvent as any).timing_mode as string | undefined;
    const resolvedTimingMode: 'date_time' | 'open' | 'anytime' | 'season' =
      storedTimingMode === 'anytime' || storedTimingMode === 'season' || storedTimingMode === 'date_time' || storedTimingMode === 'open'
        ? (storedTimingMode as 'date_time' | 'open' | 'anytime' | 'season')
        : existingEvent.event_type === 'outing'
          ? 'open'
          : 'date_time';

    const isOpenMode = resolvedTimingMode === 'open' || resolvedTimingMode === 'anytime';
    const openStartDate = resolvedTimingMode === 'open'
      ? startDateStr
      : '';
    const openEndDate = resolvedTimingMode === 'open' && hasDistinctEnd && startDateStr !== endDateStr
      ? endDateStr
      : '';

    const selectedDates = existingEvent.is_recurring && existingOccurrences
      ? existingOccurrences
          .filter(occ => occ.status === 'scheduled')
          .map(occ => format(toZonedTime(new Date(occ.start_at), TZ), 'yyyy-MM-dd'))
          .sort()
      : [];

    const rawTags = (existingEvent as any).outing_tags || [];
    const seasonTags = rawTags.filter((t: string) => t.startsWith('season:')).map((t: string) => t.replace('season:', ''));
    const nonSeasonTags = rawTags.filter((t: string) => !t.startsWith('season:'));

    form.reset({
      title: existingEvent.title || '',
      event_type: (existingEvent.event_type as EventType) || 'course',
      timing_mode: resolvedTimingMode === 'anytime' ? 'open' : resolvedTimingMode,
      selected_seasons: seasonTags,
      description: existingEvent.description || '',
      event_date: isOpenMode ? '' : startDateStr,
      start_time: isOpenMode ? '' : startTime,
      end_time: isOpenMode ? '' : endTime,
      is_multi_day: isMultiDay,
      end_date: resolvedTimingMode === 'open' ? openEndDate : (isMultiDay ? endDateStr : ''),
      ends_next_day: endsNextDay,
      start_date: resolvedTimingMode === 'open' ? openStartDate : startDateStr,
      is_recurring: existingEvent.is_recurring || false,
      selected_dates: selectedDates,
      use_producer_address: existingEvent.use_producer_address ?? true,
      custom_address: existingEvent.custom_address || '',
      price_sek: existingEvent.price_ore ? existingEvent.price_ore / 100 : 0,
      is_free: existingEvent.is_free || false,
      max_spots: existingEvent.max_spots || 10,
      booking_deadline_hours: existingEvent.booking_deadline_hours || 24,
      status: (existingEvent.status as 'draft' | 'public') || 'draft',
      booking_mode: ((existingEvent as any).booking_mode as 'none' | 'optional' | 'required') || 'none',
      audience: ((existingEvent as any).audience as 'public' | 'authenticated') || 'public',
      outing_tags: nonSeasonTags,
      learning_points: (existingEvent as any).learning_points || [],
      includes_farm_sale: (existingEvent as any).includes_farm_sale ?? false,
      includes_farm_sale_alcohol: (existingEvent as any).includes_farm_sale_alcohol ?? false,
    });

    setFormInitId(existingEvent.id);
  }, [existingEvent, existingOccurrences, isEditing]); // removed `form` from deps

  const isLoading = producerLoading || (isEditing && eventLoading);
  const isSaving = createEvent.isPending || updateEvent.isPending || syncOccurrences.isPending;

  const watchedEventType = form.watch('event_type');
  const watchedTimingMode = form.watch('timing_mode');
  const watchedTitle = form.watch('title');
  const watchedEventDate = form.watch('event_date');
  const watchedStartTime = form.watch('start_time');
  const watchedOpenFrom = form.watch('start_date');
  const watchedOpenTo = form.watch('end_date');
  const watchedEndTime = form.watch('end_time');
  const isOuting = watchedEventType === 'outing';
  const showDateSection = watchedEventType !== 'outing';
  const requiresDate = showDateSection && watchedTimingMode === 'date_time';

  const canSaveDraft = (() => {
    if (!producer) return false; // BUG 1: no save without producer context
    if (!watchedTitle || watchedTitle.trim().length < 3) return false;
    if (watchedTimingMode === 'open' && watchedOpenTo && !watchedOpenFrom) return false;
    if (requiresDate) {
      if (!watchedEventDate) return false;
      if (!watchedStartTime) return false;
    }
    return true;
  })();

  const saveDraftHint = !canSaveDraft
    ? (!producer
      ? 'Laddar gårdsinfo…'
      : (watchedTimingMode === 'open' && watchedOpenTo && !watchedOpenFrom)
        ? 'Välj Från datum först om du vill ange Till datum.'
        : 'Fyll i titel och när för att spara.')
    : undefined;

  const addHoursToTime = (time: string, hours: number): { time: string; dayOffset: number } => {
    const [h, m] = time.split(':').map(Number);
    const totalMinutes = h * 60 + m + hours * 60;
    const newH = Math.floor(totalMinutes / 60) % 24;
    const dayOffset = Math.floor(totalMinutes / 60 / 24);
    return {
      time: `${String(newH).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
      dayOffset,
    };
  };

  const addDaysToDate = (dateStr: string, days: number): string => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const TZ = 'Europe/Stockholm';

  /** Convert a date string + time string in Europe/Stockholm to a UTC ISO string */
  const toUtcIso = (dateStr: string, timeStr: string): string => {
    return fromZonedTime(`${dateStr} ${timeStr}:00`, TZ).toISOString();
  };

  const buildEventData = (data: EventFormData, statusOverride?: 'draft' | 'public') => {
    let startDateTime: string | null = null;
    let endDateTime: string | null = null;
    const isAnytimeOrSeason = data.timing_mode === 'anytime' || data.timing_mode === 'season';
    const isOpenMode = data.timing_mode === 'open';
    const openFrom = data.start_date?.trim() || '';
    const openTo = data.end_date?.trim() || '';
    const persistedTimingMode = data.timing_mode || 'date_time';

    if (isOuting || isAnytimeOrSeason) {
      const now = new Date().toISOString();
      startDateTime = now;
      endDateTime = now;
    } else if (isOpenMode) {
      if (openFrom && openTo) {
        // Open with explicit range: from date to date
        startDateTime = toUtcIso(openFrom, '00:00');
        endDateTime = toUtcIso(openTo, '23:59');
      } else if (openFrom) {
        // Open with start date only: stay open indefinitely (null end signals this)
        startDateTime = toUtcIso(openFrom, '00:00');
        endDateTime = null;
      } else {
        // Open without any dates: no start or end (canonical null semantics)
        startDateTime = null;
        endDateTime = null;
      }
    } else if (data.is_recurring && data.selected_dates.length > 0) {
      const hasExplicitEnd = !!data.end_time?.trim();
      const firstDate = data.selected_dates[0];
      const lastDate = data.selected_dates[data.selected_dates.length - 1];
      startDateTime = toUtcIso(firstDate, data.start_time);
      if (hasExplicitEnd) {
        const endDate = addDaysToDate(lastDate, 0);
        endDateTime = toUtcIso(endDate, data.end_time!);
      } else {
        endDateTime = toUtcIso(lastDate, data.start_time);
      }
    } else {
      const hasExplicitEnd = !!data.end_time?.trim();
      const eventDate = data.event_date!;
      startDateTime = toUtcIso(eventDate, data.start_time);

      if (hasExplicitEnd) {
        let endDate = eventDate;
        if (data.is_multi_day && data.end_date) {
          endDate = data.end_date;
        } else if (data.ends_next_day) {
          endDate = addDaysToDate(eventDate, 1);
        }
        endDateTime = toUtcIso(endDate, data.end_time!);
      } else {
        endDateTime = startDateTime;
      }
    }

    // Safety: if end somehow before start (shouldn't happen with Option 2 logic), clamp
    if (new Date(endDateTime) < new Date(startDateTime)) {
      endDateTime = startDateTime;
    }

    const base = {
      producer_id: farmId!,
      title: data.title,
      event_type: data.event_type,
      timing_mode: persistedTimingMode,
      description: data.description || null,
      start_date: startDateTime,
      end_date: endDateTime,
      is_recurring: data.is_recurring,
      use_producer_address: data.use_producer_address,
      custom_address: data.use_producer_address ? null : data.custom_address || null,
      price_ore: data.is_free ? 0 : Math.round(data.price_sek * 100),
      is_free: data.is_free,
      max_spots: data.max_spots,
      booking_deadline_hours: data.booking_deadline_hours,
      booking_mode: data.booking_mode || 'none',
      audience: data.audience || 'public',
      outing_tags: [
        ...(data.outing_tags || []),
        ...(data.selected_seasons || []).map(s => `season:${s}`),
      ],
      learning_points: data.event_type === 'course' ? (data.learning_points || []).filter(p => p.trim()) : [],
      includes_farm_sale: data.includes_farm_sale ?? false,
      includes_farm_sale_alcohol: data.includes_farm_sale_alcohol ?? false,
    };

    // Save ≠ Publish: only include status when an explicit statusOverride
    // is provided (publish/unpublish actions). Plain "Save" never touches status.
    if (statusOverride) {
      return { ...base, status: statusOverride as EventStatus };
    }

    return base;
  };

  const saveEvent = async (statusOverride?: 'draft' | 'public'): Promise<string | null> => {
    // Guard: past activities are immutable
    if (eventIsPast) {
      toast.error('Passerade aktiviteter kan inte ändras.');
      return null;
    }

    const data = form.getValues();
    const eventData = buildEventData(data, statusOverride);
    const isPublishAction = statusOverride === 'public';

    // P0 INVARIANT: Server guard - check place status before allowing publish
    if (isPublishAction && (!linkedPlace || !isPlacePublic(linkedPlace.status))) {
      const blockReason = getPublishBlockReason(producer, farmId, linkedPlace);
      toast.error(blockReason?.title || 'Kan inte publicera', {
        description: blockReason?.text || 'Platsen måste vara publik för att publicera aktiviteter.'
      });
      return null;
    }

    if (isPublishAction) {
      track('activity_publish_clicked', {
        activity_id: effectiveEventId ?? 'new',
        producer_id: farmId!,
        booking_mode: data.booking_mode || 'none',
        price_bucket: data.is_free ? 'free' : 'paid',
        timing_mode: data.timing_mode || 'date_time',
      });
    }

    try {
      let eventIdToUse: string;
      
      if (effectiveEventId) {
        await updateEvent.mutateAsync({ id: effectiveEventId, ...eventData });
        eventIdToUse = effectiveEventId;
      } else {
        const newEvent = await createEvent.mutateAsync(eventData);
        setNewModeEventId(newEvent.id);
        eventIdToUse = newEvent.id;
        navigate(`/app/producer/farm/${farmId}/events/${newEvent.id}/edit`, { replace: true });
      }

      try {
        
        await finalizeDraftSession(draftSessionId, 'event', eventIdToUse);
        clearDraftSessionId(`event-editor-${eventId ?? 'new'}`);
        queryClient.invalidateQueries({ queryKey: ['media-assets', 'event', eventIdToUse] });
      } catch (finalizeErr) {
        console.error('[event-save] finalize failed (non-blocking)', finalizeErr);
        const finalizeReason = classifyFailure(finalizeErr);
        track('media_finalize_failed', {
          owner_type: 'event',
          owner_id: eventIdToUse,
          failure_reason: finalizeReason,
        });
        if (shouldReportToSentry('media_finalize', finalizeReason)) {
          captureAppError(finalizeErr, {
            feature: 'media_finalize',
            action: 'finalize_draft_session',
            owner_type: 'event',
            owner_id: eventIdToUse,
            failure_reason: finalizeReason,
          });
        }
      }

      if (data.is_recurring && data.selected_dates.length > 0) {
        const syncResult = await syncOccurrences.mutateAsync({
          eventId: eventIdToUse,
          selectedDates: data.selected_dates,
          startTime: data.start_time,
          endTime: data.end_time,
        });

        if (syncResult.cantDelete.length > 0) {
          toast.warning(`${syncResult.cantDelete.length} datum har bokningar och kunde inte tas bort.`);
        }
      }

      // ── Analytics: success ──
      if (isPublishAction) {
        track('activity_publish_succeeded', { activity_id: eventIdToUse, producer_id: farmId! });

        // Fire-and-forget: trigger AI-enhanced social draft generation
        // The DB trigger creates a template draft; this enhances it with AI copy.
        supabase.functions.invoke('enhance-social-draft', {
          body: { source_type: 'event', source_id: eventIdToUse },
        }).catch(() => {
          // Best-effort — never block activity publish
        });
      } else {
        track('activity_draft_saved', { activity_id: eventIdToUse, producer_id: farmId! });
      }

      toast.success(statusOverride === 'public' ? 'Aktiviteten är publicerad' : isPublished ? 'Ändringar sparade' : 'Utkast sparat', { duration: statusOverride === 'public' ? 4000 : 2000 });
      form.reset(data);
      return eventIdToUse;
    } catch (error) {
      console.error('Failed to save:', error);

      const requestId = generateRequestId();
      const reason = classifyFailure(error);
      const featureKey = isPublishAction ? 'activity_publish' : 'activity_draft_save';
      const eventName = isPublishAction ? 'activity_publish_failed' as const : 'activity_draft_save_failed' as const;
      track(eventName, { activity_id: effectiveEventId ?? 'new', producer_id: farmId!, failure_reason: reason });

      // Operational log for admin triage
      logSystemEvent({
        level: reason === 'validation' || reason === 'guard' ? 'warn' : 'error',
        event_key: eventName,
        entity_type: 'activity',
        entity_id: effectiveEventId ?? undefined,
        producer_id: farmId!,
        message: `Activity ${isPublishAction ? 'publish' : 'save'} failed: ${reason}`,
        request_id: requestId,
        context: buildSystemEventContext({ feature: isPublishAction ? 'activity_publish' : 'activity_draft_save', action: isPublishAction ? 'publish' : 'save', reason_kind: reason as any, surface: 'event_editor' }),
      });

      if (shouldReportToSentry(featureKey, reason)) {
        captureAppError(error, {
          feature: featureKey,
          action: 'save',
          event_id: effectiveEventId ?? 'new',
          producer_id: farmId!,
          failure_reason: reason,
          request_id: requestId,
        });
      }
      
      if (error instanceof PlanLimitError) {
        toast.error(error.message, {
          description: 'Uppgradera för fler aktiviteter.',
          duration: 6000,
        });
      } else {
        const parsed = parsePublishError(error);
        toast.error(parsed.title, {
          description: parsed.text,
          duration: 6000,
        });
      }
      return null;
    }
  };

  const handleSaveDraft = async () => {
    if (!canSaveDraft) return;
    const isFormValid = await form.trigger();
    if (!isFormValid) {
      toast.error('Kontrollera fälten och försök igen');
      return;
    }
    const savedId = await saveEvent('draft');
    if (savedId) {
      navigate(`/app/producer/farm/${farmId}/events`, { replace: true });
    }
  };

  const handlePublish = async () => {
    const isFormValid = await form.trigger();
    if (!isFormValid) {
      toast.error('Fyll i alla obligatoriska fält för att publicera');
      return;
    }
    const data = form.getValues();
    const savedId = await saveEvent('public');
    if (savedId) {
      // Show publish success prompt instead of navigating away immediately
      setPublishedEventData({
        id: savedId,
        title: data.title,
        startDate: data.event_date ? data.event_date : null,
      });
      track('publish_success_prompt_shown', { activity_id: savedId, producer_id: farmId! });
      setShowPublishSuccess(true);
    }
  };

  const handleSaveChanges = async (statusOverride?: 'draft' | 'public') => {
    const isFormValid = await form.trigger();
    if (!isFormValid) {
      toast.error('Fyll i alla obligatoriska fält');
      return;
    }

    // Check for critical changes on published events with bookings
    if (isPublished && bookedCount > 0 && existingEvent && !statusOverride) {
      const newData = buildEventData(form.getValues());
      const changes = getCriticalChanges(existingEvent as unknown as Record<string, unknown>, newData);
      if (changes.length > 0) {
        setCriticalFields(changes);
        setCriticalLabels(getCriticalChangeLabels(changes));
        setPendingSaveStatus(undefined);
        setShowCriticalDialog(true);
        return;
      }
    }

    await saveEvent(statusOverride);
  };

  const handleCriticalSaveAndNotify = async () => {
    setShowCriticalDialog(false);
    const savedId = await saveEvent(pendingSaveStatus);
    if (savedId) {
      // Fire-and-forget notification
      try {
        await supabase.functions.invoke('notify-event-changes', {
          body: {
            event_id: savedId,
            changed_fields: criticalFields,
            event_title: form.getValues('title'),
          },
        });
      } catch (err) {
        console.warn('Failed to send change notifications (non-blocking)', err);
      }
    }
  };

  const handleCriticalSaveWithout = async () => {
    setShowCriticalDialog(false);
    await saveEvent(pendingSaveStatus);
  };

  const handleUnpublish = async () => {
    await saveEvent('draft');
    setShowUnpublishDialog(false);
    toast.success('Aktiviteten är nu avpublicerad');
  };

  const handleCancel = () => {
    requestNavigate(() => navigate(`/app/producer/farm/${farmId}/events`));
  };

  const handleClone = async () => {
    if (!existingEvent) return;
    try {
      const cloned = await cloneActivity.mutateAsync(existingEvent as any);
      toast.success('Skapade en ny aktivitet baserad på den tidigare. Kontrollera datum och tid innan du publicerar.');
      navigate(`/app/producer/farm/${farmId}/events/${cloned.id}/edit`);
    } catch (err) {
      console.error('Clone failed:', err);
      toast.error('Kunde inte skapa kopia');
    }
  };

  if (!farmId) return null;

  if (isLoading) {
    return (
      <ProducerLayout farmId={farmId} producerName={producer?.name} title="Laddar...">
        <div className="space-y-6 max-w-2xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </ProducerLayout>
    );
  }

  // ── Template selection handler ──
  const handleTemplateSelect = (template: ActivityTemplate) => {
    const prefill = template.prefill;
    Object.entries(prefill).forEach(([key, value]) => {
      form.setValue(key as any, value as any, { shouldDirty: false, shouldTouch: false });
    });
    setSelectedTemplate(template);
    setTemplatePicked(true);
    track('activity_template_selected', { template_id: template.id, producer_id: farmId });
  };

  const handleBlankStart = () => {
    setSelectedTemplate(null);
    setTemplatePicked(true);
    track('activity_template_selected', { template_id: 'blank', producer_id: farmId });
  };

  const handleChangeTemplate = () => {
    setTemplatePicked(false);
    setSelectedTemplate(null);
    form.reset({ ...defaultEventFormValues, event_type: initialEventType as any });
  };

  const mode = isEditing ? 'edit' : 'new';

  // ── Template picker (new mode only) ──
  if (!templatePicked && !isEditing) {
    return (
      <ProducerLayout
        farmId={farmId}
        producerName={producer?.name}
        title="Skapa aktivitet"
      >
        <div className="max-w-3xl mx-auto pb-16">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Tillbaka</span>
          </button>
          <ActivityTemplatePicker
            offeringKeys={offeringKeys}
            onSelect={handleTemplateSelect}
            onBlank={handleBlankStart}
          />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId}
      producerName={producer?.name}
      title={eventIsPast ? 'Passerad aktivitet' : isEditing ? 'Redigera aktivitet' : 'Skapa aktivitet'}
    >
      <div className="max-w-2xl lg:max-w-6xl mx-auto pb-32 lg:pb-8">
        {/* Mobile back link */}
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline mb-4 lg:hidden"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Tillbaka</span>
        </button>

        {mode === 'edit' && !eventIsPast && isPublished && existingEvent && (
          <div className="mb-4 lg:hidden">
            <EntityShareAction
              entityType="activity"
              publicUrl={activityPublicUrl}
              embedSnippet={activityEmbedSnippet}
              socialDescription="Skapa underlag för inlägg i dina sociala kanaler."
              socialAction={features.enableShareStudio ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowShareStudio(true)}
                >
                  <Sparkles className="h-4 w-4" />
                  Share Studio
                </Button>
              ) : (
                <ShareKit
                  eventId={existingEvent.id}
                  eventTitle={existingEvent.title}
                  producerId={farmId || ''}
                  producerSlug={producer?.slug}
                  startDate={existingEvent.start_date}
                  endDate={existingEvent.end_date}
                  isFree={existingEvent.is_free}
                  priceOre={existingEvent.price_ore}
                  bookingMode={(existingEvent as any).booking_mode || 'none'}
                  timingMode={(existingEvent as any).timing_mode}
                />
              )}
              buttonClassName="w-full gap-1.5"
            />
          </div>
        )}

        {/* Desktop back link (new mode – no sticky bar) */}
        {mode === 'new' && (
          <button
            type="button"
            onClick={handleCancel}
            className="hidden lg:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Tillbaka</span>
          </button>
        )}

        {/* Past activity banner */}
        {eventIsPast && (
          <div className="rounded-lg border border-border bg-muted/50 p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">Passerad</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Den här aktiviteten har redan ägt rum och kan inte längre ändras.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="ml-auto shrink-0 gap-1.5"
              onClick={handleClone}
              disabled={cloneActivity.isPending}
            >
              <Copy className="h-3.5 w-3.5" />
              Skapa ny från denna
            </Button>
          </div>
        )}

        {/* Desktop top action bar */}
        {mode === 'edit' && !eventIsPast && (
          <div className="hidden lg:flex items-center justify-between gap-2 mb-8 sticky top-0 z-30 bg-background/80 backdrop-blur-sm py-3 -mx-2 px-2 border-b border-border/40">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              <span>Tillbaka</span>
            </button>
            <div className="flex items-center gap-2">
            {isPublished && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowUnpublishDialog(true)}
                disabled={isSaving}
                className="h-9 text-xs text-destructive hover:text-destructive"
              >
                <EyeOff className="h-3.5 w-3.5 mr-1.5" />
                Avpublicera
              </Button>
            )}
            {isPublished && existingEvent && (
              <EntityShareAction
                entityType="activity"
                publicUrl={activityPublicUrl}
                embedSnippet={activityEmbedSnippet}
                socialDescription="Skapa underlag för inlägg i dina sociala kanaler."
                socialAction={features.enableShareStudio ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setShowShareStudio(true)}
                  >
                    <Sparkles className="h-4 w-4" />
                    Share Studio
                  </Button>
                ) : (
                  <ShareKit
                    eventId={existingEvent.id}
                    eventTitle={existingEvent.title}
                    producerId={farmId || ''}
                    producerSlug={producer?.slug}
                    startDate={existingEvent.start_date}
                    endDate={existingEvent.end_date}
                    isFree={existingEvent.is_free}
                    priceOre={existingEvent.price_ore}
                    bookingMode={(existingEvent as any).booking_mode || 'none'}
                    timingMode={(existingEvent as any).timing_mode}
                  />
                )}
                buttonClassName="h-9 text-xs gap-1.5"
              />
            )}
            {!isPublished && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handlePublish}
                        disabled={isSaving || !publishAllowed || (isPublished && !isDirty)}
                        className="h-9 text-xs border-primary text-primary hover:bg-primary/10"
                      >
                        <Globe className="h-3.5 w-3.5 mr-1.5" />
                        Publicera
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!publishAllowed && publishBlock?.text && (
                    <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                      {publishBlock.text}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <SaveButton
              type="button"
              size="sm"
              onClick={() => handleSaveChanges()}
              disabled={isSaving || !isDirty}
              isSaving={isSaving}
              savingLabel="Sparar…"
              className="h-9 text-xs"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Spara
            </SaveButton>
            </div>
          </div>
        )}

        {/* Desktop top action bar — CREATE mode */}
        {mode === 'new' && (
          <div className="hidden lg:flex items-center justify-end gap-2 mb-8">
            {saveDraftHint && (
              <p className="text-xs text-muted-foreground mr-auto">{saveDraftHint}</p>
            )}
            <SaveButton
              type="button"
              size="sm"
              onClick={handleSaveDraft}
              disabled={!canSaveDraft}
              isSaving={isSaving}
              savingLabel="Sparar…"
              className="h-9 text-xs"
              data-testid="event-save-draft-desktop"
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              Spara utkast
            </SaveButton>
          </div>
        )}
        {/* Publish block – calm amber hint, not a red error */}
        {publishBlock && !(producer?.suspended_at) && producer?.status !== 'public' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-4 mb-4" data-testid="publish-block-hint">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{publishBlock.title}</p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{publishBlock.text}</p>
            {publishBlock.cta && (
              <Link
                to={publishBlock.cta.route}
                className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-amber-800 dark:text-amber-200 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-100"
              >
                {publishBlock.cta.label} →
              </Link>
            )}
          </div>
        )}
        {/* Suspended/banned – keep destructive for actual moderation blocks */}
        {publishBlock && (producer?.suspended_at || producer?.producer_status === 'paused' || producer?.producer_status === 'banned') && (
          <Alert variant="destructive" className="border-destructive/30 mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{publishBlock.title}</AlertTitle>
            <AlertDescription>{publishBlock.text}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()}>
            <fieldset disabled={eventIsPast} className="contents">
            <ActivityComposer
              form={form}
              mode={mode}
              eventId={effectiveEventId}
              draftSessionId={draftSessionId}
              producerId={farmId}
              requiresDate={showDateSection}
              isPublished={isPublished}
              publishBlock={publishBlock}
              coverUpload={coverUpload}
              galleryUpload={galleryUpload}
              producerHasFarmSale={canOfferFarmSale}
              producerHasAlcohol={canOfferAlcohol}
              templateLabel={selectedTemplate?.label ?? null}
              onChangeTemplate={!isEditing ? handleChangeTemplate : undefined}
              devProducerCapDebug={import.meta.env.DEV && producer ? {
                has_farm_sale_alcohol: producer.has_farm_sale_alcohol ?? false,
                has_farm_shop: producer.has_farm_shop ?? false,
              } : null}
            />
            </fieldset>
          </form>
        </Form>

        {/* Stats + Share — only for published activities */}
        {isPublished && existingEvent && (
          <div className="mt-8 space-y-6">
            <ActivityStatsPanel eventId={existingEvent.id} />

            <ShareStudioSheet
              open={showShareStudio}
              onOpenChange={setShowShareStudio}
              target={{
                type: 'activity',
                id: existingEvent.id,
                slug: (existingEvent as any).slug || null,
                title: existingEvent.title,
                heroImageUrl: coverAsset.url640 || null,
                producerName: producer?.name || '',
                producerSlug: producer?.slug || '',
                startDate: existingEvent.start_date,
                endDate: existingEvent.end_date,
                timingMode: (existingEvent as any).timing_mode,
                isFree: existingEvent.is_free,
                priceOre: existingEvent.price_ore,
                bookingMode: (existingEvent as any).booking_mode || 'none',
                venueText: (existingEvent as any).custom_address || null,
                descriptionHtml: existingEvent.description || null,
              }}
            />
          </div>
        )}
      </div>

      {/* Composer footer — hidden for past activities */}
      {!eventIsPast && (
        <ActivityComposerFooter
          mode={mode}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          onSaveChanges={handleSaveChanges}
          onPublish={handlePublish}
          onUnpublish={isPublished ? () => setShowUnpublishDialog(true) : undefined}
          isSaving={isSaving}
          isDirty={isDirty}
          canSaveDraft={canSaveDraft}
          saveDraftHint={saveDraftHint}
          isPublished={isPublished}
          publishAllowed={publishAllowed}
          publishBlockReason={publishBlock?.text}
        />
      )}

      <UnsavedChangesDialog
        open={isConfirmOpen}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />

      <AlertDialog open={showUnpublishDialog} onOpenChange={setShowUnpublishDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avpublicera aktivitet</AlertDialogTitle>
            <AlertDialogDescription>
              Aktiviteten blir ett utkast och döljs för besökare.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnpublish}>
              Avpublicera
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CriticalChangesDialog
        open={showCriticalDialog}
        onOpenChange={setShowCriticalDialog}
        bookedCount={bookedCount}
        changedLabels={criticalLabels}
        onSaveAndNotify={handleCriticalSaveAndNotify}
        onSaveWithout={handleCriticalSaveWithout}
        isSaving={isSaving}
      />

      {/* Publish Success → Share Studio Lite flow */}
      <PublishSuccessPrompt
        open={showPublishSuccess}
        onOpenChange={(open) => {
          setShowPublishSuccess(open);
          if (!open && !showShareLite) {
            navigate(`/app/producer/farm/${farmId}/events`, { replace: true });
          }
        }}
        eventTitle={publishedEventData?.title || ''}
        eventDate={publishedEventData?.startDate}
        onShare={() => setShowShareLite(true)}
      />

      {publishedEventData && producer && (
        <ShareStudioLite
          open={showShareLite}
          onOpenChange={(open) => {
            setShowShareLite(open);
            if (!open) {
              navigate(`/app/producer/farm/${farmId}/events`, { replace: true });
            }
          }}
          target={mapActivityToShareTarget({
            eventId: publishedEventData.id,
            title: publishedEventData.title,
            startDate: publishedEventData.startDate,
            isFree: form.getValues('is_free'),
            priceOre: Math.round(form.getValues('price_sek') * 100),
            bookingMode: form.getValues('booking_mode') || 'none',
            venueText: form.getValues('use_producer_address') ? (producer.name || '') : (form.getValues('custom_address') || null),
            heroImageUrl: coverAsset.url640 || null,
            producerName: producer.name || '',
            producerSlug: producer.slug || '',
            producerId: producer.id,
            timingMode: form.getValues('timing_mode'),
          })}
        />
      )}
    </ProducerLayout>
  );
}
